"use client";

import * as React from "react";
import { useDragScroll } from "@/hooks/use-drag-scroll";

/**
 * A section row that SCROLLS below `lg` and is a 3-up grid at `lg`.
 *
 * The home page's short sections carry exactly three cards. Stacked on a phone
 * that is three full-width blocks to scroll past for one section, and on a
 * tablet a 2-up grid left the third card alone on a second row with a gap
 * beside it. QA: "for any sections that have 3 article/video items only, e.g.
 * 醫生會客室, 運動懶人包, make them horizontally scrollable instead, with
 * snapping effect".
 *
 * So below `lg` it is a snap carousel — roughly one-and-a-bit cards in view on
 * a phone, two-and-a-bit on a tablet, so the next one is always visibly there —
 * and at `lg` it is the grid it always was.
 *
 * The negative margin lets the strip bleed to the screen edge while its first
 * card stays aligned with the section's text; the padding puts the gutter back
 * inside the scroller so the last card can reach the left edge.
 *
 * `scroll-pl-6` matches that padding. Without it the snap position ignores the
 * gutter: the scroller settles with the first card flush to the SCREEN edge —
 * 24px out of line with the heading above it — and rests at `scrollLeft: 24`
 * instead of 0 the moment it mounts.
 *
 * `useDragScroll` is the same hook the LJ and EJ Tech carousels use: it settles
 * on a card when a CURSOR drag is released (CSS snapping alone leaves a slow
 * half-drag wherever it stopped) and swallows the click that would otherwise
 * follow a drag, so dragging across a card does not open it. It ignores touch
 * pointers, so a finger gets native scrolling — which is why the CSS snap below
 * has to be here rather than left to the hook.
 *
 * `snap-proximity`, not `snap-mandatory`: mandatory forbids the scroller from
 * resting anywhere but a snap point, so it fights the hook's own settle after a
 * mouse drag and re-snaps behind it. Proximity engages when a touch release
 * lands near a card, which is the gesture this is for.
 */
export function SectionSnapRow({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const drag = useDragScroll<HTMLDivElement>(ref, {
    snapToChildren: true,
    childSelector: "[data-snap-item]",
  });

  return (
    <div
      ref={ref}
      {...drag.dragProps}
      className={`no-scrollbar -mx-6 flex snap-x snap-proximity gap-6 overflow-x-auto px-6 scroll-pl-6 lg:scroll-pl-0 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-visible lg:px-0 ${drag.dragClass} ${className}`}
    >
      {React.Children.map(children, (child, i) => (
        <div
          key={i}
          data-snap-item
          className="w-[78%] shrink-0 snap-start sm:w-[45%] lg:w-auto lg:shrink"
        >
          {child}
        </div>
      ))}
    </div>
  );
}
